import { useState, useEffect, useRef, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import styled from '@emotion/styled';
import lottie, { AnimationItem } from 'lottie-web';
import { subscriptionStore } from '@/stores/SubscriptionStore';
import { paywallFlowStore } from '@/stores/PaywallFlowStore';
import { isApplePayCancelledError, useTrialPayment } from '@/hooks/useTrialPayment';
import { usePolicyText } from '@/hooks/usePolicyText';
import { sendLoadSuccess, sendPaymentFailed } from '@/hooks/useWebViewMessaging';
import { CtaButton } from '@/components/ui/CtaButton';
import { PaymentError } from '@/components/ui/PaymentError';
import { V1Desc } from '@/components/paywall/V1Desc';
import { RestoreSubscription } from '@/components/paywall/RestoreSubscription';
import { LegalLinks } from '@/components/paywall/LegalLinks';
import paywallAnim from '@/assets/animations/paywall_v3.json';
import checkEmojiUrl from '@/assets/apple-emoji/check.png?url';
import { syncPaywallMetaFromUrl } from '@/utils/paywallUrlParams';

const Screen = styled.div`
  max-width: 430px;
  margin: 0 auto;
  padding:
    var(--app-safe-area-top)
    calc(16px + var(--app-safe-area-right))
    calc(16px + var(--app-safe-area-bottom))
    calc(16px + var(--app-safe-area-left));
`;

const Hero = styled.section`
  display: grid;
  text-align: center;
`;

const Subtitle = styled.div`
  font-weight: 500;
  font-size: 28px;
  letter-spacing: 0.87px;
  text-align: center;
  background: #31bcf9;
  padding: 2px 8px;
  margin-bottom: 4px;
  display: grid;
  margin-top: 28px;
  color: white;
  width: fit-content;
  justify-self: center;
  border-radius: 6px;
`;

const Title = styled.h1`
  margin: 0px;
  font-weight: 500;
  font-size: 21px;
  letter-spacing: 0.87px;
  text-align: center;
  margin-bottom: 40px;
`;

const Art = styled.div`
  display: grid;
  place-items: center;
`;

const ProgressGroup = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 16px;
  margin-bottom: 40px;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  font-weight: 600;
  letter-spacing: 0.87px;
  text-align: center;
`;

const Accent = styled.span<{ $color: string }>`
  color: ${({ $color }) => $color};
`;

const PercentSlot = styled.span`
  display: inline-block;
  width: 3ch;
  text-align: right;
  font-variant-numeric: tabular-nums;
`;

const Pitch = styled.h4`
  font-weight: 500;
  font-size: 28px;
  letter-spacing: 0.87px;
  text-align: center;
  max-width: 238px;
  margin: 0 auto 16px;
`;

const Linkish = styled.span`
  color: #31bcf9;
`;

const TrialCheck = styled.img`
  display: inline-block;
  width: 22px;
  height: 22px;
  margin-left: 4px;
  object-fit: contain;
  vertical-align: -4px;
`;

const CtaSection = styled.section`
  padding: 0 16px 16px;
`;

export const PaywallV3 = observer(() => {
  const [searchParams] = useSearchParams();
  const lottieElRef = useRef<HTMLDivElement>(null);
  const animRef = useRef<AnimationItem | null>(null);
  const [percent, setPercent] = useState(0);
  const [accentColor, setAccentColor] = useState('#ff6333');
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [isPageLoading, setIsPageLoading] = useState(true);
  const applePayLoadStatusReportedRef = useRef(false);

  const {
    initializeApplePay,
    showApplePay,
    preferredPaymentMethod,
    isInitializing,
    isApplePayReady,
    trialPlan,
    trialPlans,
    selectTrialPlan,
    refreshPaymentRequest,
    handlePaymentError,
  } = useTrialPayment();

  const {
    trialDays,
    trialPriceFormatted,
  } = usePolicyText();

  useEffect(() => {
    let onEnterFrame: (() => void) | null = null;

    const init = async () => {
      // Load plans and initialize Apple Pay
      if (!subscriptionStore.plans || subscriptionStore.plans.length === 0) {
        await subscriptionStore.fetchPlans();
      }

      // Initialize Apple Pay after plans are loaded
      const applePayReady = await initializeApplePay();

      // Show error if Apple Pay is not available
      if (!applePayReady) {
        const errorMsg = 'Apple Pay is not available on this device. Please use Safari on iOS or macOS.';
        setError(errorMsg);
      }

      if (!applePayLoadStatusReportedRef.current) {
        applePayLoadStatusReportedRef.current = true;
        if (applePayReady) {
          sendLoadSuccess();
        }
        else {
          sendPaymentFailed('Apple Pay is not available on this device. Please use Safari on iOS or macOS.');
        }
      }

      // Load Lottie animation
      if (lottieElRef.current && !animRef.current) {
        animRef.current = lottie.loadAnimation({
          container: lottieElRef.current,
          renderer: 'svg',
          loop: true,
          autoplay: true,
          animationData: paywallAnim,
        });

        // Listen to animation frames to update percentage
        onEnterFrame = () => {
          if (!animRef.current) return;
          const totalFrames = animRef.current.getDuration(true);
          if (!totalFrames || totalFrames <= 0) return;
          const current = Math.min(Math.max(animRef.current.currentFrame, 0), totalFrames);
          const ratio = current / totalFrames;
          const newPercent = Math.round(ratio * 100);
          setPercent(newPercent);
          setAccentColor(
            newPercent <= 33 ? '#ff6333' : newPercent <= 67 ? '#ffe817' : '#46eb3a',
          );
        };

        if (animRef.current) {
          animRef.current.addEventListener('enterFrame', onEnterFrame);
        }
      }

      // Page loading is complete
      setIsPageLoading(false);

      syncPaywallMetaFromUrl(searchParams);
    };

    void init();

    return () => {
      if (animRef.current && onEnterFrame) {
        animRef.current.removeEventListener('enterFrame', onEnterFrame);
      }
      animRef.current?.destroy();
    };
  }, [initializeApplePay, searchParams]);

  const weeklyTrialPlan = useMemo(() => {
    return (
      trialPlans.find((plan) => plan.billingFrequency === 'weekly')
      ?? trialPlan
      ?? null
    );
  }, [trialPlans, trialPlan]);

  useEffect(() => {
    if (!weeklyTrialPlan?.planId) {
      return;
    }

    if (weeklyTrialPlan.planId !== trialPlan?.planId) {
      selectTrialPlan(weeklyTrialPlan.planId);
    }
  }, [weeklyTrialPlan?.planId, trialPlan?.planId, selectTrialPlan]);

  useEffect(() => {
    if (!trialPlan?.planId) {
      return;
    }

    void refreshPaymentRequest();
  }, [trialPlan?.planId, refreshPaymentRequest]);

  const onCta = () => {
    if (processing || isInitializing || !isApplePayReady) return;

    if (!preferredPaymentMethod) {
      const errorMsg = 'Apple Pay is not available on this device. Please use Safari on iOS or macOS.';
      setError(errorMsg);
      sendPaymentFailed(errorMsg);
      return;
    }

    setError('');
    setProcessing(true);

    void showApplePay(paywallFlowStore.meta.trim() || undefined).catch((err: unknown) => {
      if (isApplePayCancelledError(err)) {
        return;
      }

      console.error('Payment process failed:', err);
      setError(handlePaymentError(err));
    }).finally(() => {
      setProcessing(false);
    });
  };

  return (
    <Screen>
      <Hero>
        <Subtitle>Unleash</Subtitle>
        <Title>Full VPN Protection</Title>

        <Art aria-hidden="true">
          <div ref={lottieElRef} style={{ width: '100%', height: '224px' }} />
        </Art>

        <ProgressGroup>
          <Accent $color={accentColor}>
            <PercentSlot>{percent}</PercentSlot>
            % &nbsp;
          </Accent>
          PROTECTION ACTIVE
        </ProgressGroup>

        <Pitch>
          Tap
          {' '}
          <Linkish>Continue</Linkish>
          <br />
          for
          {' '}
          {trialDays}
          ‑Day Trial
          {' '}
          {trialPriceFormatted}
          {' '}
          <TrialCheck src={checkEmojiUrl} alt="" aria-hidden="true" />
        </Pitch>
      </Hero>

      <CtaSection>
        <CtaButton
          loading={processing || isInitializing || isPageLoading}
          disabled={processing || isInitializing || !isApplePayReady || isPageLoading}
          onClick={onCta}
        >
          Continue
        </CtaButton>
        <PaymentError error={error} />
      </CtaSection>

      <V1Desc />
      <RestoreSubscription />
      <LegalLinks termsHref="/policy/terms" privacyHref="/policy/privacy" />
    </Screen>
  );
});
